export type ClusterMember = {
    id: string;
    label: string;
    role: string;
    state: string;
    host: string;
    timeline: string;
    lag: string;
    isLeader: boolean;
};

type MemberCandidate = {
    value: Record<string, unknown>;
    fallbackId?: string;
};

function asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown>
        : {};
}

function getValue(record: Record<string, unknown>, key: string): unknown {
    if (key in record) return record[key];
    const matchingKey = Object.keys(record).find((candidate) => candidate.toLowerCase() === key.toLowerCase());
    return matchingKey ? record[matchingKey] : undefined;
}

function firstText(record: Record<string, unknown>, keys: string[]): string {
    for (const key of keys) {
        const value = getValue(record, key);
        if (typeof value === 'string' && value.trim()) return value.trim();
        if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    }
    return '';
}

function displayName(member: string) {
    const normalized = member.toLowerCase();
    if (normalized === 'aus' || normalized.includes('australia')) return 'Australia';
    if (normalized === 'sg' || normalized === 'sin' || normalized.includes('singapore')) return 'Singapore';
    if (normalized === 'home' || normalized.includes('home')) return 'Home';
    return member;
}

function looksLikeMember(record: Record<string, unknown>) {
    return [
        'name',
        'member',
        'node',
        'role',
        'state',
        'status',
        'host',
        'api_url',
        'is_leader',
    ].some((key) => getValue(record, key) !== undefined);
}

function candidatesFromCollection(value: unknown): MemberCandidate[] {
    if (Array.isArray(value)) {
        return value
            .map(asRecord)
            .filter(looksLikeMember)
            .map((member) => ({ value: member }));
    }

    const record = asRecord(value);
    return Object.entries(record).flatMap(([key, memberValue]) => {
        const member = asRecord(memberValue);
        return looksLikeMember(member) ? [{ value: member, fallbackId: key }] : [];
    });
}

function findMemberCandidates(value: unknown, depth = 0): MemberCandidate[] {
    if (depth > 5) return [];

    const directArrayCandidates = candidatesFromCollection(value);
    if (Array.isArray(value) && directArrayCandidates.length > 0) return directArrayCandidates;

    const record = asRecord(value);
    const collectionKeys = ['members', 'nodes', 'servers', 'instances', 'cluster'];
    for (const key of collectionKeys) {
        const collection = getValue(record, key);
        const candidates = candidatesFromCollection(collection);
        if (candidates.length > 0) return candidates;
    }

    const mappedCandidates = candidatesFromCollection(record);
    if (mappedCandidates.length > 0) return mappedCandidates;

    const wrapperKeys = ['data', 'result', 'payload', 'response'];
    for (const key of wrapperKeys) {
        const nestedValue = getValue(record, key);
        if (nestedValue !== undefined) {
            const candidates = findMemberCandidates(nestedValue, depth + 1);
            if (candidates.length > 0) return candidates;
        }
    }

    for (const nestedValue of Object.values(record)) {
        if (!nestedValue || typeof nestedValue !== 'object') continue;
        const candidates = findMemberCandidates(nestedValue, depth + 1);
        if (candidates.length > 0) return candidates;
    }

    return [];
}

function findLeaderId(value: unknown, depth = 0): string {
    if (depth > 5) return '';
    const record = asRecord(value);
    for (const key of ['currentLeader', 'current_leader', 'leader', 'primary']) {
        const candidate = getValue(record, key);
        if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
    }

    for (const nestedValue of Object.values(record)) {
        if (!nestedValue || typeof nestedValue !== 'object') continue;
        const leader = findLeaderId(nestedValue, depth + 1);
        if (leader) return leader;
    }
    return '';
}

export function extractPatroniMembers(payload: unknown): ClusterMember[] {
    const leaderId = findLeaderId(payload).toLowerCase();
    const seen = new Set<string>();

    return findMemberCandidates(payload).flatMap(({ value: member, fallbackId }) => {
        const id = firstText(member, ['name', 'member', 'id', 'node']) || fallbackId || '';
        if (!id || seen.has(id.toLowerCase())) return [];
        seen.add(id.toLowerCase());

        const role = firstText(member, ['role', 'member_role', 'type']) || 'unknown';
        const roleValue = role.toLowerCase();
        const isLeader = getValue(member, 'isLeader') === true
            || getValue(member, 'is_leader') === true
            || getValue(member, 'leader') === true
            || ['leader', 'primary', 'master'].includes(roleValue)
            || (leaderId !== '' && leaderId === id.toLowerCase());

        return [{
            id,
            label: displayName(id),
            role,
            state: firstText(member, ['state', 'status', 'health']) || 'unknown',
            host: firstText(member, ['host', 'api_url', 'apiUrl', 'conn_url', 'connUrl', 'url']) || '—',
            timeline: firstText(member, ['timeline', 'tl']) || '—',
            lag: firstText(member, ['lag', 'replication_lag', 'replicationLag', 'lag_in_mb', 'lagInMb', 'lag in mb']) || '—',
            isLeader,
        }];
    });
}
